import Head from 'next/head';

export interface PageHeadMeta {
    title: string;
    description: string;
    img?: string; // full CDN URL from cdnUrl(); absent → OG/Twitter tags omitted
}

interface PageHeadProps {
    // Static pages: pass a typed meta object from metaData.tsx
    meta?: PageHeadMeta;
    // Dynamic/CMS pages: pass resolved primitives (used when meta is absent or as overrides)
    title?: string;
    description?: string;
    image?: string; // absolute OG image URL
    // Canonical: either url (path, e.g. "/about-us") or canonical (full absolute URL)
    url?: string;
    canonical?: string; // full absolute URL — bypasses domain+url computation
    // Optional overrides for CMS pages where og values differ from page title/description
    ogTitle?: string;
    ogDescription?: string;
    ogLocale?: string; // e.g. "en_US" — omitted on static pages, set on CMS pages
    preloadImage?: string;
    schema?: string; // pre-serialized JSON-LD string
    minimal?: boolean; // explicitly suppress OG/Twitter when meta.img is set but page is minimal by design
    noindex?: boolean; // emit noindex,nofollow override for pages that must not be indexed (e.g. /thank-you)
    noHreflang?: boolean; // suppress the hreflang alternate links for pages that must not advertise locale alternates
}

const ogImageType = (img?: string): string => {
    const ext = img?.split('?')[0].split('.').pop()?.toLowerCase();
    if (ext === 'png') return 'image/png';
    if (ext === 'webp') return 'image/webp';
    return 'image/jpeg';
};

const ensureAbsolute = (domain: string, img?: string): string | undefined => {
    if (!img) return undefined;
    // Some CDN folders contain spaces; browsers tolerate them in <img src>, link-preview crawlers don't.
    const url = /^https?:\/\//i.test(img) ? img : `${domain}/${img.replace(/^\//, '')}`;
    return url.replace(/ /g, '%20');
};

const PageHead = ({
    meta,
    title,
    description,
    image,
    url,
    canonical: canonicalProp,
    ogTitle,
    ogDescription,
    ogLocale,
    preloadImage,
    schema,
    minimal,
    noindex,
    noHreflang
}: PageHeadProps) => {
    const domain = (process.env.NEXT_PUBLIC_DOMAIN ?? '').replace(/\/$/, '');

    const resolvedTitle = title ?? meta?.title ?? '';
    const resolvedDescription = description ?? meta?.description ?? '';
    const resolvedCanonical = canonicalProp ?? `${domain}${url ?? ''}`;

    // Ensure og:image is always absolute — guard against any relative img slipping through.
    const ogImage = ensureAbsolute(domain, image ?? meta?.img);

    const resolvedOgTitle = ogTitle ?? resolvedTitle;
    const resolvedOgDescription = ogDescription ?? resolvedDescription;
    const showOg = !minimal && !!ogImage;

    return (
        <Head>
            <title>{resolvedTitle}</title>
            <meta name="description" content={resolvedDescription} />
            <meta name="viewport" content="width=device-width, initial-scale=1" />
            {noindex && <meta name="robots" content="noindex,nofollow" />}
            {showOg && <meta content={ogImageType(ogImage)} property="og:image:type" />}
            {showOg && <meta property="og:title" content={resolvedOgTitle} />}
            {showOg && <meta property="og:url" content={resolvedCanonical} />}
            {showOg && <meta property="og:type" content="website" />}
            {showOg && <meta property="og:image" content={ogImage} />}
            {showOg && <meta property="og:description" content={resolvedOgDescription} />}
            {showOg && <meta property="og:image:width" content="1200" />}
            {showOg && <meta property="og:image:height" content="630" />}
            {showOg && <meta property="og:site_name" content="Debut Infotech" />}
            {showOg && ogLocale && <meta property="og:locale" content={ogLocale} />}
            {showOg && <meta name="twitter:card" content="summary_large_image" />}
            {showOg && <meta name="twitter:title" content={resolvedOgTitle} />}
            {showOg && <meta name="twitter:description" content={resolvedOgDescription} />}
            {showOg && <meta name="twitter:url" content={resolvedCanonical} />}
            {showOg && <meta name="twitter:image" content={ogImage} />}
            {showOg && <meta name="twitter:site" content="@DebutInfotech" />}
            <link rel="icon" href="/favicon.ico" />
            <link rel="canonical" href={resolvedCanonical} />
            {!noHreflang && <link rel="alternate" href={resolvedCanonical} hrefLang="x-default" />}
            {!noHreflang && <link rel="alternate" href={resolvedCanonical} hrefLang="en-us" />}
            {!noHreflang && <link rel="alternate" href={resolvedCanonical} hrefLang="en-gb" />}
            {!noHreflang && <link rel="alternate" href={resolvedCanonical} hrefLang="en-ca" />}
            {preloadImage && <link rel="preload" href={preloadImage} as="image" />}
            {schema && <script type="application/ld+json" dangerouslySetInnerHTML={{__html: schema}} />}
        </Head>
    );
};

export default PageHead;
